/**
 * @fileoverview Theme Management Composable
 * @description Manages application theme state with localStorage persistence
 *
 * Features:
 * - Reactive theme state (system/light/dark)
 * - localStorage persistence
 * - Apply dark class to document root
 * - Listen for system preference changes
 * - Automatic theme application
 *
 * @example
 * import { useTheme } from '@/composables/useTheme'
 *
 * const { theme, setTheme, isDark } = useTheme()
 */

import { ref, computed, watch } from 'vue'

const STORAGE_KEY = 'theme'

/**
 * Get system theme preference
 * @returns {boolean} True if system prefers dark mode
 */
function getSystemTheme() {
  return window.matchMedia('(prefers-color-scheme: dark)').matches
}

/**
 * Initialize theme from localStorage or system preference
 * @returns {string} Theme value (system, light, dark)
 */
function getInitialTheme() {
  const stored = localStorage.getItem(STORAGE_KEY)
  if (stored && ['system', 'light', 'dark'].includes(stored)) {
    return stored
  }
  return 'system'
}

// Shared state across all component instances
const theme = ref(getInitialTheme())

/**
 * Apply theme to document root
 * @param {string} themeValue - Theme value (system, light, dark)
 */
function applyTheme(themeValue) {
  const root = document.documentElement

  if (themeValue === 'system') {
    const prefersDark = getSystemTheme()
    root.classList.toggle('dark', prefersDark)
  } else {
    root.classList.toggle('dark', themeValue === 'dark')
  }
}

/**
 * Composable for managing theme state
 * @returns {{
 *   theme: import('vue').Ref<string>,
 *   setTheme: (newTheme: string) => void,
 *   isDark: import('vue').ComputedRef<boolean>
 * }}
 */
export function useTheme() {
  /**
   * Set theme and persist to localStorage
   * @param {string} newTheme - Theme value (system, light, dark)
   */
  const setTheme = newTheme => {
    if (!['system', 'light', 'dark'].includes(newTheme)) {
      console.warn(`Invalid theme value: ${newTheme}`)
      return
    }

    theme.value = newTheme
    localStorage.setItem(STORAGE_KEY, newTheme)
    applyTheme(newTheme)
  }

  /**
   * Check if current theme is dark
   * @type {import('vue').ComputedRef<boolean>}
   */
  const isDark = computed(() => {
    if (theme.value === 'system') {
      return getSystemTheme()
    }
    return theme.value === 'dark'
  })

  return {
    theme,
    setTheme,
    isDark
  }
}

/**
 * Initialize theme on module load
 */
applyTheme(theme.value)

/**
 * Watch for system theme changes
 */
const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
mediaQuery.addEventListener('change', () => {
  if (theme.value === 'system') {
    applyTheme('system')
  }
})
